import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Settings, X } from 'lucide-react';
import DoctorShell from '../components/DoctorShell';
import NivaLogo from '../../components/NivaLogo';
import { useLanguage } from '../../contexts/LanguageContext';
import { useDoctorSession } from '../contexts/DoctorSessionContext';
import { doctorNotePath, ROUTES } from '../../constants/routes';
import { doctorGetNote, doctorListNotes } from '../services/doctorApi';
import { downloadDoctorClinicalNotePdf } from '../services/doctorClinicalNotePdf';

const FILTERS = [
  { id: 'all', labelKey: 'doctorFilterAll' },
  { id: 'first_visit', labelKey: 'doctorFilterFirstVisit' },
  { id: 'follow_up', labelKey: 'doctorFilterFollowUp' },
  { id: 'procedure', labelKey: 'doctorFilterProcedure' },
  { id: 'emergency', labelKey: 'doctorFilterEmergency' },
];

const TYPE_LABELS = {
  first_visit: 'First Visit',
  follow_up: 'Follow-up',
  procedure: 'Procedure',
  emergency: 'Emergency',
};

export default function DoctorNotesScreen() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { doctor, showToast } = useDoctorSession();
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [langPickerNoteId, setLangPickerNoteId] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const hasActiveFilters = Boolean(search.trim() || dateFilter || filter !== 'all');

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await doctorListNotes({
          noteType: filter,
          q: search.trim(),
          date: dateFilter || undefined,
        });
        if (!cancelled) setNotes(data.notes || []);
      } catch {
        if (!cancelled) setNotes([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [filter, search, dateFilter]);

  const handleDownloadLang = async (lang) => {
    if (!langPickerNoteId || downloading) return;
    setDownloading(true);
    try {
      const note = await doctorGetNote(langPickerNoteId);
      await downloadDoctorClinicalNotePdf(note, {
        lang,
        doctorName: doctor?.full_name || '',
        clinicName: doctor?.clinic_name || '',
        clinicLogoUrl: doctor?.clinic_logo_url || '',
      });
      setLangPickerNoteId(null);
    } catch (err) {
      showToast(err.message || t('doctorDownloadFailed'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <DoctorShell>
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-surface px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <NivaLogo variant="header" />
          <button
            type="button"
            onClick={() => navigate(ROUTES.DOCTOR_SETTINGS)}
            className="flex h-10 w-10 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100"
            aria-label={t('doctorSettings')}
          >
            <Settings className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>
        <h1 className="mt-3 mb-3 text-xl font-bold text-slate-900">{t('doctorNotes')}</h1>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('doctorSearchNotes')}
          className="mb-3 w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm"
        />
        <div className="mb-3 flex items-center gap-2">
          <label className="sr-only" htmlFor="doctor-notes-date">
            {t('doctorFilterDate')}
          </label>
          <input
            id="doctor-notes-date"
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-slate-900"
          />
          {dateFilter ? (
            <button
              type="button"
              onClick={() => setDateFilter('')}
              aria-label={t('doctorClearDate')}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600"
            >
              <X className="h-4 w-4" strokeWidth={2.25} />
            </button>
          ) : null}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold ${
                filter === f.id ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {t(f.labelKey)}
            </button>
          ))}
        </div>
      </header>

      <div className="space-y-2 px-4 py-4">
        {loading && <p className="text-center text-sm text-gray-400">…</p>}
        {!loading && notes.length === 0 && (
          <p className="rounded-xl bg-white px-4 py-8 text-center text-sm text-gray-500 ring-1 ring-gray-100">
            {hasActiveFilters ? t('doctorNoNotesForFilters') : t('doctorNoNotesYet')}
          </p>
        )}
        {notes.map((n) => (
          <div
            key={n.id}
            className="flex w-full items-center gap-2 rounded-xl bg-white px-4 py-3 ring-1 ring-gray-100"
          >
            <button
              type="button"
              onClick={() => navigate(doctorNotePath(n.id))}
              className="min-w-0 flex-1 text-left"
            >
              <p className="font-semibold text-slate-900">{n.patient_name}</p>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-primary-light px-2 py-0.5 text-[11px] font-semibold text-primary">
                  {TYPE_LABELS[n.note_type] || n.note_type}
                </span>
                <span className="text-xs text-gray-500">
                  {n.created_at
                    ? new Date(n.created_at).toLocaleDateString(
                        language === 'ar' ? 'ar-JO' : 'en-GB'
                      )
                    : ''}
                </span>
              </div>
              {n.diagnosis_preview && (
                <p className="mt-1 text-xs text-gray-500">{n.diagnosis_preview}</p>
              )}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLangPickerNoteId(n.id);
              }}
              aria-label={t('doctorDownloadNote')}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-light text-primary"
            >
              <Download className="h-5 w-5" strokeWidth={2.25} />
            </button>
          </div>
        ))}
      </div>

      {langPickerNoteId && (
        <div className="fixed inset-0 z-[300] flex flex-col justify-end bg-black/50">
          <div className="rounded-t-2xl bg-white px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4">
            <p className="mb-1 text-base font-bold text-slate-900">{t('doctorDownloadNote')}</p>
            <p className="mb-4 text-sm text-gray-500">
              {downloading ? t('doctorTranslatingNote') : t('doctorDownloadLanguagePrompt')}
            </p>
            <div className="space-y-2">
              <button
                type="button"
                disabled={downloading}
                onClick={() => handleDownloadLang('en')}
                className="w-full rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
              >
                {downloading ? '…' : t('english')}
              </button>
              <button
                type="button"
                disabled={downloading}
                onClick={() => handleDownloadLang('ar')}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 disabled:opacity-60"
              >
                {downloading ? '…' : t('arabic')}
              </button>
              <button
                type="button"
                disabled={downloading}
                onClick={() => setLangPickerNoteId(null)}
                className="w-full py-3 text-sm font-medium text-gray-500"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </DoctorShell>
  );
}
